import { apiClient } from '../client';

/**
 * Endpoints relacionados à autenticação e usuários
 */

/**
 * Realiza login de um usuário
 * @param {string} login - Login do usuário
 * @param {string} password - Senha do usuário
 * @returns {Promise<Object>} Dados do usuário e token
 */
const login = async (login, password) => {
  const { data } = await apiClient.post('/users/login', { login, password });
  return data;
};

/**
 * Realiza login de nível de acesso (admin, teacher, etc)
 * @param {string} login - Login do nível de acesso
 * @param {string} password - Senha do nível de acesso
 * @returns {Promise<Object>} Dados do usuário e token
 */
const loginAccessLevel = async (login, password) => {
  const { data } = await apiClient.post('/access/login', { login, password });
  return data;
};

/**
 * Cadastra novos usuários (estudantes e responsáveis)
 * @param {Array<string|number>} registrations - Array de números de matrícula
 * @returns {Promise<Object>} Resultado do cadastro
 */
const signIn = async (registrations) => {
  const { data } = await apiClient.post('/users/signin', {
    registration: Array.isArray(registrations) ? registrations : [registrations],
  });
  return data;
};

/**
 * Obtém informações de acesso (credenciais) para uma matrícula
 * @param {string|number} registration - Número de matrícula
 * @returns {Promise<Object>} Informações de acesso
 */
const getAccessData = async (registration) => {
  if (!registration) {
    throw new Error('Registro não pode ser vazio');
  }
  const { data } = await apiClient.post('/users/info', { registration });
  return data;
};

/**
 * Lista todos os usuários cadastrados
 * @returns {Promise<Array>} Lista de usuários
 */
const getUsers = async () => {
  const { data } = await apiClient.get('/users');
  return data.users || [];
};

/**
 * Obtém apenas estudantes
 * @returns {Promise<Array>} Lista de estudantes
 */
const getStudents = async () => {
  const users = await getUsers();
  return users.filter((user) => user.type === 'STUDENT');
};

/**
 * Obtém apenas responsáveis
 * @returns {Promise<Array>} Lista de responsáveis
 */
const getResponsables = async () => {
  const users = await getUsers();
  return users.filter((user) => user.type === 'RESPONSABLE');
};

/**
 * Obtém usuários com token de notificação
 * @returns {Promise<Array>} Lista de usuários com token
 */
const getUsersWithNotificationToken = async () => {
  const users = await getUsers();
  return users.filter((user) => user.tokenNotification !== null);
};

/**
 * Atualiza o token de notificação de um usuário
 * @param {string} tokenNotification - Token de notificação
 * @param {string|number} idSponte - ID do usuário no Sponte
 * @returns {Promise<Object>} Resultado da atualização
 */
const updateNotificationToken = async (tokenNotification, idSponte) => {
  const { data } = await apiClient.put('/token/notifications', {
    tokenNotification,
    idSponte: String(idSponte),
  });
  return data;
};

/**
 * Altera senha do usuário (obrigatório no primeiro login)
 * @param {number} userId - ID do usuário
 * @param {string} currentPassword - Senha atual
 * @param {string} newPassword - Nova senha
 * @returns {Promise<Object>} Resultado da alteração
 */
const changePassword = async (userId, currentPassword, newPassword) => {
  const { data } = await apiClient.post('/access/change-password', {
    userId,
    currentPassword,
    newPassword,
  });
  return data;
};

/**
 * Reseta senha do usuário (admin nas configurações)
 * @param {number} userId - ID do usuário
 * @param {string} newPassword - Nova senha
 * @returns {Promise<Object>} Resultado do reset
 */
const resetPassword = async (userId, newPassword) => {
  const { data } = await apiClient.post('/access/reset-password', {
    userId,
    newPassword,
  });
  return data;
};

export {
  login,
  loginAccessLevel,
  signIn,
  getAccessData,
  getUsers,
  getStudents,
  getResponsables,
  getUsersWithNotificationToken,
  updateNotificationToken,
  changePassword,
  resetPassword,
};

